import { useEffect, useState, useCallback, useContext, memo, useMemo } from 'react'
import {
  BBox,
  Feature,
  FeatureCollection,
  Geometry,
  MultiPolygon,
  Polygon,
  GeoJsonProperties,
} from 'geojson'
import { MapContext } from './mapContext'
import GeoJSON from './geoJson'
import { FeatureCollectionWithOptionalInfoAndStyle } from '~/components/mapContent/utils/geoAndStatsToFeatureCollection'
import { h3ResolutionResolveByZoom } from '~/prix/utils/h3ResolutionResolve'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import { NeoClusterPosition } from '../map/mapPosition'
import { useStyles } from '~/components/mapContent/utils/hooks/useStylesForLegends'
import { useCustomLabel } from './hook/useCustomLabel'
import booleanIntersects from '@turf/boolean-intersects'
import transformScale from '@turf/transform-scale'
import bboxPolygon from '@turf/bbox-polygon'
import { debounce } from 'debounce'
import { getHexagonSizeOnScreen } from '../map/hexagon'
import bbox from '@turf/bbox'
import { formatNumberAsCluster } from '../map/formatNumberAsCluster'
import { ActionCall } from '~/prix/react/hooks/concurrentActions'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import centerOfMass from '@turf/center-of-mass'
import { GenericObject } from '~/prix/types/type'
import {
  calculateH3Groups,
  callActionLoadHexagon,
  createHexagonFeature,
  getInitialMaxResolution,
  getMaxResolution,
  getRange,
  getUnitFormat,
  handleClickHexagon,
  NeoClusterClickReturn,
} from '../map/utils/neoCluster.utils'
import { ClusterProps } from '../map/neoCluster'

const NeoCluster = ({
  positions,
  layers,
  layerIndex,
  layerRequest,
  delimitation,
  onClickCluster,
  onChangeLegend,
}: ClusterProps) => {
  const mapContext = useContext(MapContext)
  if (!mapContext) {
    throw new Error('Cluster must be used within a GoogleMaps map')
  }
  const { map, google: googleMaps } = mapContext

  const [queryFeature, setQueryFeature] = useState<Feature<Polygon | MultiPolygon, GenericObject> | null>(null)
  const [isVisibleLabels, setIsVisibleLabels] = useState(false)
  const [currentResolution, setCurrentResolution] = useState<number>(6)
  const [h3Groups, setH3Groups] = useState<Record<string, NeoClusterPosition[]>>({})
  const [hexagonFeatures, setHexagonFeatures] = useState<
    FeatureCollection<Polygon | MultiPolygon, GenericObject>
  >({
    type: 'FeatureCollection',
    features: [],
  })

  const INITIAL_MAX_RESOLUTION = getInitialMaxResolution(layers[layerIndex].space.delimitationType)
  const MAX_RESOLUTION = getMaxResolution(positions)

  const unitFormat = getUnitFormat(layers[layerIndex].variable)

  const ranges = useMemo(() => {
    const range = getRange(h3Groups, unitFormat)

    if (onChangeLegend) debounce(() => onChangeLegend(layerIndex, range), 200)()

    return layers.map((layer, index) => (index === layerIndex ? range : { min: 0, max: 0 }))
  }, [layers, h3Groups])

  const { hexagonStyles } = useStyles({ layers, ranges })

  const { createCustomLabel } = useCustomLabel()
  const customLabel = createCustomLabel()

  const updateHexagons = useCallback(() => {
    if (!map) return

    const style = hexagonStyles[layerIndex]

    const features = createHexagonFeature(
      h3Groups,
      unitFormat,
      style,
      delimitation,
      renderVisibleHexagons,
      currentResolution,
      INITIAL_MAX_RESOLUTION,
    )

    handleShowNumbers()

    setHexagonFeatures({
      type: 'FeatureCollection',
      features,
    })
  }, [h3Groups, currentResolution])

  const renderVisibleHexagons = useCallback(
    (feature: Geometry | Feature<any, GenericObject>) => {
      if (!map || !googleMaps) return

      const bounds = map.getBounds()
      const viewportBBox = [
        bounds?.getSouthWest().lng(),
        bounds?.getSouthWest().lat(),
        bounds?.getNorthEast().lng(),
        bounds?.getNorthEast().lat(),
      ] as BBox

      try {
        const viewportPolygon = bboxPolygon(viewportBBox)
        const expandedViewportPolygon = transformScale(viewportPolygon, 1.2)

        return booleanIntersects(expandedViewportPolygon, feature)
      } catch {
        return false
      }
    },
    [map, googleMaps],
  )

  const handleShowNumbers = useCallback(() => {
    if (!map) return

    const center = map.getCenter()
    const zoom = map.getZoom()
    if (typeof zoom !== 'number' || !center) {
      return
    }

    const size = getHexagonSizeOnScreen(currentResolution, zoom, center.lat())
    setIsVisibleLabels(size >= 15)
  }, [map, currentResolution])

  const updateH3Groups = useCallback(() => {
    const h3Groups = calculateH3Groups(positions, currentResolution)

    setH3Groups(h3Groups)
    return h3Groups
  }, [positions, currentResolution])

  const updateLabelMarkers = () => {
    if (!map || !googleMaps || !customLabel || !isVisibleLabels) return

    return hexagonFeatures.features.map(feature => {
      if (feature.properties!.isRemainder === true) {
        return null
      }
      const center = centerOfMass(feature).geometry.coordinates
      const label = new customLabel(
        {
          lat: center[1],
          lng: center[0],
        },
        feature.properties!.isLoading
          ? 'Carregando...'
          : `
          <div 
            style="text-align: center; justify-content: center; filter: drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.5));"
          >
            ${
              String(formatNumberAsCluster(Number(feature.properties!.value ?? 0))) +
              (unitFormat === 'percentage' ? '%' : '') +
              (feature.properties?.customLegend ? `<br/> ${feature.properties?.customLegend}` : '')
            }
          </div>


        `,
      )
      label.setMap(map)

      return label
    })
  }

  useEffect(() => {
    if (!map || !googleMaps) return

    const markers = updateLabelMarkers()

    return () => {
      markers?.map((marker: any) => marker?.setMap(null))
    }
  }, [map, googleMaps, hexagonFeatures, isVisibleLabels])

  useEffect(() => {
    updateH3Groups()
  }, [positions, currentResolution])

  useEffect(() => {
    updateHexagons()
  }, [h3Groups])

  useEffect(() => {
    if (!map || !googleMaps) return
    updateH3Groups()
  }, [map, googleMaps])

  useEffect(() => {
    if (!map || !googleMaps) return

    const handleMoveEnd = debounce(() => {
      if (!map || !googleMaps) return

      const currentZoom = map.getZoom()
      const newResolution = h3ResolutionResolveByZoom(currentZoom, undefined, MAX_RESOLUTION)

      if (newResolution === currentResolution) {
        updateHexagons()
        return
      }

      setCurrentResolution(newResolution)
    }, 300)

    const handleZoomStart = () => {
      setIsVisibleLabels(false)
    }

    const listenerMoveEnd = map.addListener('bounds_changed', handleMoveEnd)
    const listenerZoomStart = map.addListener('zoom_changed', handleZoomStart)
    return () => {
      listenerMoveEnd.remove()
      listenerZoomStart.remove()
    }
  }, [map, googleMaps, currentResolution, h3Groups])

  const handleClick = useCallback(
    (event: google.maps.Data.MouseEvent) => {
      const h3 = event.feature.getProperty('h3') as string
      const feature = hexagonFeatures.features.find((f: any) => f.properties!.h3 === h3)

      if (!feature || !map || !googleMaps) {
        return
      }

      const currentZoom = map.getZoom() ?? 8
      const hexFeature = hexagonFeatures.features.find((f: any) => f.properties!.h3 === h3)
      if (!hexFeature) {
        return
      }

      const actionClick = handleClickHexagon(
        currentZoom,
        MAX_RESOLUTION,
        h3,
        feature,
        hexagonFeatures,
      ) as NeoClusterClickReturn

      switch (actionClick) {
        case NeoClusterClickReturn.hexagonNotFound:
          return

        case NeoClusterClickReturn.queryFeature:
          setQueryFeature(hexFeature)
          break

        case NeoClusterClickReturn.maxZoom:
          if (onClickCluster) {
            onClickCluster(
              feature as Feature<
                Polygon | MultiPolygon,
                {
                  h3: string
                  value: number
                  preservedAreaPercentage: number
                }
              >,
            )
          }
          break

        default:
        case NeoClusterClickReturn.zoomToHexagon:
          zoomToHexagon(feature)
          break
      }
    },
    [map, hexagonFeatures, onClickCluster],
  )

  const { callAction } = useActionFunction(layerRequest.actionDefinition)

  const showH3Loading = useCallback(
    (index: number) => {
      setHexagonFeatures((prevData: any) => {
        prevData.features[index].properties!.fillColor = 'rgba(0,0,0,0.8)'
        prevData.features[index].properties!.strokeColor = 'rgba(0,0,0,0.8)'
        prevData.features[index].properties!.isLoading = true
        return { ...prevData }
      })
    },
    [map, hexagonFeatures],
  )

  useEffect(() => {
    if (!queryFeature || !layerRequest) return

    getResolutionFeatures(queryFeature)
  }, [layerRequest, queryFeature])

  const getResolutionFeatures = useCallback(
    (feature: Feature<Polygon | MultiPolygon>) => {
      callActionLoadHexagon(
        feature,
        positions,
        layerRequest as any,
        hexagonFeatures,
        callAction,
        showH3Loading,
        updateH3Groups,
        zoomToHexagon,
      )
    },
    [layerRequest, positions, hexagonFeatures],
  )

  const zoomToHexagon = (feature: Feature) => {
    if (!map) return

    try {
      const [minLng, minLat, maxLng, maxLat] = bbox(feature)

      const bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(minLat, minLng),
        new google.maps.LatLng(maxLat, maxLng),
      )

      // if (
      //   map.getBounds() &&
      //   map.getBounds()?.contains(bounds.getNorthEast()) &&
      //   map.getBounds()?.contains(bounds.getSouthWest())
      // ) {
      //   const newZoom = Math.min(map.getZoom()! + 2, 21)
      //   const center = bounds.getCenter()
      //   map.setZoom(newZoom)
      //   map.setCenter(center)
      //   return
      // }

      map.fitBounds(bounds, 10)
    } catch (error) {
      console.error('Erro ao processar a geometria:', error)
    }
  }

  return (
    <GeoJSON
      geoJson={hexagonFeatures as FeatureCollectionWithOptionalInfoAndStyle}
      onClick={handleClick}
    />
  )
}

export default memo(NeoCluster)
